import { createReducer, on, Action } from '@ngrx/store';
import { ConsultationUtils } from '@care-portals/data';
import * as ConsultationActions from './consultation.actions';
import {
  Answers,
  Consultation,
  Files,
  IFile
} from '@care-portals/api-interfaces';

export const FEATURE_KEY = 'consultation';

export interface State {
  loading: boolean; // has the Consultation list been loading
  uploading: boolean;
  saving: boolean;
  error?: any; // last none error (if any)
  _id: string;
  answers: Answers;
  files: Files;
  changedFiles: { [resourceId: string]: boolean };
  orderId: number | null;
  forms: {
    required: string[];
    selectedIndex: number;
  };
  questions: {
    required: Array<any>;
    questionStack: Array<string>;
    nextQuestion: string;
    qs: Array<number>;
  };
  draftConsults: Consultation[];
  completed: boolean;
  redirectUrl: string | null;
  /**
   * Where to hand the patient over to Pathwai once patient-web is done with its
   * own (legacy) questionnaires. Null when this org has no migrated
   * questionnaires outstanding. See `organization.engage`.
   */
  pathwaiHandoffUrl: string | null;
}

export const initialState: State = {
  // set initial required properties
  loading: false,
  uploading: false,
  saving: false,
  error: null,
  _id: '',
  answers: {},
  files: {},
  changedFiles: {},
  orderId: null,
  forms: {
    required: [],
    selectedIndex: 0
  },
  questions: {
    required: [],
    questionStack: [],
    nextQuestion: null,
    qs: []
  },
  draftConsults: [],
  completed: false,
  redirectUrl: null,
  pathwaiHandoffUrl: null
};

const consultationReducer = createReducer(
  initialState,

  on(ConsultationActions.getRequiredForms, (state, { query }) => ({
    ...state,
    loading: true,
    error: null,
    orderId: query.orderId ? query.orderId : null,
    redirectUrl: query.redirectUrl ? query.redirectUrl : null,
    pathwaiHandoffUrl: null,
    forms: {
      required: [],
      selectedIndex: 0
    }
  })),

  on(ConsultationActions.getRequiredFormsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(
    ConsultationActions.getRequiredFormsSuccess,
    (state, { consultations, pathwaiStartUrl, incompleteConsults }) => {
      // Questionnaires already migrated to Pathwai must not be rendered here —
      // only the legacy ones stay in `forms.required`.
      const pathwaiConsults = (consultations || []).filter(
        c => c.engine === 'pathwai'
      );
      const legacyTypes = (consultations || [])
        .filter(c => c.engine !== 'pathwai')
        .map(c => c.type);

      return {
        ...state,
        loading: false,
        forms: {
          required: legacyTypes,
          selectedIndex: 0
        },
        // Pathwai works out the rest for itself from here — which flows to
        // run, in what order, and where to land afterwards.
        pathwaiHandoffUrl: pathwaiConsults.length ? pathwaiStartUrl || null : null,
        draftConsults: incompleteConsults
      };
    }
  ),

  on(ConsultationActions.loadRequiredQuestions, state => ({
    ...state,
    loading: true,
    error: null
  })),

  on(
    ConsultationActions.loadRequiredQuestionsSuccess,
    (state, { requiredQuestions }) => ({
      ...state,
      questions: {
        selectedIndex: 0,
        required: requiredQuestions || [],
        questionStack: [
          ConsultationUtils.getQuestionTypeFromNode(requiredQuestions)
        ],
        nextQuestion: null,
        qs: [1]
      },
      loading: false
    })
  ),

  on(ConsultationActions.nextQuestionSuccess, (state, { questionType, qs }) => {
    let newStack = state.questions.questionStack;
    newStack = [...newStack, questionType];
    return {
      ...state,
      questions: {
        ...state.questions,
        questionStack: newStack,
        qs
      }
    };
  }),

  on(ConsultationActions.setNextQuestion, (state, { questionType }) => {
    return {
      ...state,
      questions: {
        ...state.questions,
        nextQuestion: questionType
      }
    };
  }),

  on(ConsultationActions.prevQuestion, state => {
    let newStack = state.questions.questionStack;
    newStack = newStack.slice(0, newStack.length - 1);
    return {
      ...state,
      questions: {
        ...state.questions,
        questionStack: newStack,
        qs: ConsultationUtils.getIndexOfQuestion(newStack[newStack.length - 1])
      },
      completed: false
    };
  }),

  on(ConsultationActions.setDraftConsult, (state, { draftConsult }) => {
    return {
      ...state,
      answers: Object.assign({}, draftConsult.answers),
      _id: draftConsult._id,
    };
  }),

  on(ConsultationActions.storeAnswer, (state, { states }) => ({
    ...state,
    answers: Object.assign({}, state.answers, {
      states //: { ...state.answers?.states, ...states }
    })
  })),

  on(ConsultationActions.setAnswer, (state, { answer }) => ({
    ...state,
    answers: Object.assign({}, state.answers, { [answer.field]: answer.data })
  })),

  on(ConsultationActions.setRawFile, (state, { resourceId, fileItem }) => ({
    ...state,
    files: {
      ...state.files,
      [resourceId]: { ...fileItem, loaded: true } as IFile
    },
    changedFiles: {
      ...state.changedFiles,
      [resourceId]: !!fileItem.buffer
    }
  })),

  on(ConsultationActions.getFile, (state, { resourceId }) => ({
    ...state,
    files: {
      ...state.files,
      [resourceId]: { buffer: '', loaded: false } as IFile
    },
    error: null
  })),
  on(ConsultationActions.getFileSuccess, (state, { file, resourceId }) => {
    return {
      ...state,
      files: {
        ...state.files,
        [resourceId]: { ...file, loaded: true } as IFile
      }
    };
  }),
  on(ConsultationActions.getFileFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(ConsultationActions.saveFile, state => {
    return {
      ...state,
      uploading: true
    };
  }),

  on(ConsultationActions.saveFileSuccess, (state, { resourceId, fileItem }) => {
    const newAnswer = {
      [resourceId]: {
        ...state.answers[resourceId],
        [resourceId]: fileItem.location
      }
    };

    return {
      ...state,
      answers: Object.assign({}, state.answers, newAnswer),
      changedFiles: {
        ...state.changedFiles,
        [resourceId]: false
      },
      uploading: false
    };
  }),

  on(ConsultationActions.saveFileFailure, state => {
    return {
      ...state,
      uploading: false
    };
  }),

  on(ConsultationActions.saveFiles, state => {
    return {
      ...state,
      uploading: true
    };
  }),

  on(ConsultationActions.saveFilesSuccess, (state, { files }) => {
    const newAnswer = {};
    const newChangedFiles = {};
    for (const item of files) {
      newAnswer[item.resourceId] = item.file.location;
      newChangedFiles[item.resourceId] = false;
    }

    return {
      ...state,
      answers: { ...state.answers, ...newAnswer },
      changedFiles: { ...state.changedFiles, ...newChangedFiles },
      uploading: false
    };
  }),

  on(ConsultationActions.saveFilesFailure, state => {
    return {
      ...state,
      uploading: false
    };
  }),

  on(ConsultationActions.saveForm, state => {
    return {
      ...state,
      saving: true
    };
  }),

  on(
    ConsultationActions.saveFormSuccess,
    (state, { completed, consultation }) => {
      if (completed) {
        const isLastForm = state.forms.selectedIndex === state.forms.required.length - 1;
        return {
          ...state,
          //! Don't disable loader on last form, we want to keep it active until patient is redirected to patient portal
          saving: isLastForm,
          _id: '',
          answers: {},
          completed
        };
      }

      return {
        ...state,
        saving: false,
        _id: consultation._id,
        completed: false,
        answers: consultation.answers
      };
    }
  ),

  on(ConsultationActions.saveFormFailure, (state, { error }) => {
    return {
      ...state,
      saving: false,
      error
    };
  }),

  on(ConsultationActions.nextForm, state => {
    return {
      ...state,
      forms: {
        ...state.forms,
        selectedIndex: state.forms.selectedIndex + 1
      },
      answers: {}
    };
  }),

  on(ConsultationActions.deleteDraftConsult, state => ({
    ...state
  })),

  on(ConsultationActions.deleteDraftConsultSuccess, (state, { formType }) => {
    const draftConsults = [...state.draftConsults];
    const index = draftConsults.findIndex(consult => consult.type === formType);
    if (index > -1) {
      draftConsults.splice(index, 1);
    }

    return {
      ...state,
      draftConsults
    };
  }),

  on(ConsultationActions.deleteDraftConsultFailure, (state, { error }) => ({
    ...state,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return consultationReducer(state, action);
}
